import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { X, Upload, Box, Check, AlertCircle, RotateCcw, Eye, EyeOff, Layers, ArrowUpDown, ArrowLeftRight, Circle, Info, ChevronDown } from 'lucide-react';
import { loadModel, analyzeModel, convertToCutList, mergeSimilarPieces, autoDetectUnit, getModelBounds, groupByThickness } from '../utils/meshAnalyzer';
import { generate3DEdgeSVG, capture3DSnapshot } from '../utils/modelToSVG';
import { useAuth } from '../contexts/AuthContext';
import { PALETTE } from '../utils/colors';

// Unit conversion factors and display info
const UNIT_INFO = {
  mm: { label: 'Millimeters', short: 'mm', gridSize: 10, gridDivisions: 10 },
  cm: { label: 'Centimeters', short: 'cm', gridSize: 10, gridDivisions: 10 },
  m: { label: 'Meters', short: 'm', gridSize: 1, gridDivisions: 10 },
  in: { label: 'Inches', short: 'in', gridSize: 12, gridDivisions: 12 },
  ft: { label: 'Feet', short: 'ft', gridSize: 1, gridDivisions: 12 }
};

// Map from CutListContext unit names to Model3DImporter unit keys
const mapContextUnitToImporterUnit = (contextUnit) => {
  const mapping = {
    'inches': 'in',
    'feet': 'ft',
    'mm': 'mm',
    'cm': 'cm',
    'm': 'm'
  };
  return mapping[contextUnit] || 'in';
};

// Conversion factors to inches (internal base unit for conversion)
const UNIT_TO_INCHES = {
  'mm': 1 / 25.4,
  'cm': 10 / 25.4,
  'm': 1000 / 25.4,
  'in': 1,
  'ft': 12
};

// Convert value from one unit to another
const convertUnit = (value, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return value;
  // Convert to inches first, then to target unit
  const inches = value * UNIT_TO_INCHES[fromUnit];
  return inches / UNIT_TO_INCHES[toUnit];
};

// Get display symbol for a unit
const getUnitSymbol = (unit) => {
  const symbols = { 'mm': 'mm', 'cm': 'cm', 'm': 'm', 'in': 'in', 'ft': 'ft' };
  return symbols[unit] || unit;
};

// Build PIECE_COLORS from shared palette for 3D view compatibility
const PIECE_COLORS = PALETTE.map(c => ({
  hex: c.threeHex,
  css: null,    // We use inline styles instead
  border: null, // We use inline styles instead
  cssHex: c.hex,
}));

/**
 * 3D Model Importer Component
 * Allows users to import OBJ/GLTF files and extract flat pieces for the cut list
 */
// Create a text sprite for grid labels
const createTextSprite = (text, position, color = '#666666') => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = 128;
  canvas.height = 64;

  context.fillStyle = color;
  context.font = 'bold 24px Arial';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, 64, 32);

  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
  const sprite = new THREE.Sprite(material);
  sprite.position.copy(position);

  return sprite;
};

const Model3DImporter = ({ isOpen, onClose, onImport, initialFile, initialUnit }) => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const modelRef = useRef(null);
  const gridRef = useRef(null);
  const gridLabelsRef = useRef([]);
  const grainLinesRef = useRef([]);
  const pieceOverlaysRef = useRef([]);
  const animationRef = useRef(null);
  const materialTargetsRef = useRef(new Map()); // For smooth opacity transitions
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const pieceMeshMapRef = useRef(new Map()); // Maps mesh UUIDs to piece IDs
  const isDraggingRef = useRef(false);
  const mouseDownPosRef = useRef({ x: 0, y: 0 });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [detectedPieces, setDetectedPieces] = useState([]);
  const [modelUnit, setModelUnit] = useState(initialUnit ? mapContextUnitToImporterUnit(initialUnit) : 'in');
  const [modelBounds, setModelBounds] = useState(null);
  const [targetThickness, setTargetThickness] = useState(19);
  const [useThicknessFilter, setUseThicknessFilter] = useState(false);
  const [showAllPieces, setShowAllPieces] = useState(true);
  const [mergeSimilar, setMergeSimilar] = useState(true);
  const [groupByThicknessEnabled, setGroupByThicknessEnabled] = useState(true);
  const [isObjFile, setIsObjFile] = useState(false);
  const [showObjHint, setShowObjHint] = useState(false);
  const [hoveredPieceId, setHoveredPieceId] = useState(null);
  const [tooltipInfo, setTooltipInfo] = useState(null); // { x, y, name }
  const [settingsCollapsed, setSettingsCollapsed] = useState(false);

  // Display unit (from app settings) - separate from model's detected unit
  const displayUnit = initialUnit ? mapContextUnitToImporterUnit(initialUnit) : 'in';

  // Auth for free tier gating
  const { checkImportAllowed } = useAuth();

  // Update grid based on unit and model size
  const updateGrid = useCallback((unit, bounds) => {
    if (!sceneRef.current) return;

    // Remove old grid
    if (gridRef.current) {
      sceneRef.current.remove(gridRef.current);
    }

    // Remove old labels
    gridLabelsRef.current.forEach(label => sceneRef.current.remove(label));
    gridLabelsRef.current = [];

    const unitInfo = UNIT_INFO[unit];
    let gridSize, majorDivisions;

    if (bounds) {
      const maxDim = bounds.maxDimension;
      const targetSize = maxDim * 2.5;

      // Calculate nice round grid size
      const magnitude = Math.pow(10, Math.floor(Math.log10(targetSize)));
      gridSize = Math.ceil(targetSize / magnitude) * magnitude;
      majorDivisions = Math.min(gridSize / (magnitude / 2), 20);
    } else {
      gridSize = unitInfo.gridSize * 10;
      majorDivisions = 10;
    }

    const gridY = bounds ? -bounds.size.y / 2 - 0.01 : 0;

    // Create grid
    const newGrid = new THREE.GridHelper(gridSize, majorDivisions * 2, 0x666666, 0xaaaaaa);
    newGrid.position.y = gridY;
    sceneRef.current.add(newGrid);
    gridRef.current = newGrid;
  }, []);

  // Zoom camera to fit model bounds
  const zoomToFit = useCallback((bounds) => {
    if (!cameraRef.current || !controlsRef.current || !bounds) return;

    const camera = cameraRef.current;
    const controls = controlsRef.current;

    const { size, center } = bounds;
    const maxDim = Math.max(size.x, size.y, size.z);

    // Calculate distance to fit model in view
    const fov = camera.fov * (Math.PI / 180);
    const distance = maxDim / (2 * Math.tan(fov / 2)) * 1.5;

    // Position camera at an angle
    const angle = Math.PI / 4;
    camera.position.set(
      center.x + distance * Math.sin(angle),
      center.y + distance * 0.7,
      center.z + distance * Math.cos(angle)
    );

    controls.target.copy(center);
    controls.update();

    // Update camera far plane if needed
    if (distance * 3 > camera.far) {
      camera.far = distance * 5;
      camera.updateProjectionMatrix();
    }
  }, []);

  // Initialize Three.js scene
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      45,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      100000
    );
    camera.position.set(200, 200, 200);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controlsRef.current = controls;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(100, 200, 100);
    scene.add(directionalLight);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight2.position.set(-100, -100, -100);
    scene.add(directionalLight2);

    // Initial grid (simple, will be replaced when model loads)
    const gridHelper = new THREE.GridHelper(10, 10, 0x888888, 0xcccccc);
    scene.add(gridHelper);
    gridRef.current = gridHelper;

    // Animation loop with smooth opacity interpolation
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      controls.update();

      // Smoothly interpolate material opacities toward targets
      const lerpSpeed = 0.15;
      materialTargetsRef.current.forEach((target, material) => {
        if (material.opacity !== target) {
          material.opacity += (target - material.opacity) * lerpSpeed;
          // Snap to target if very close
          if (Math.abs(material.opacity - target) < 0.01) {
            material.opacity = target;
          }
        }
      });

      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
      controls.dispose();
      renderer.dispose();
      // Clean up grid labels, grain lines, and material targets
      gridLabelsRef.current = [];
      grainLinesRef.current = [];
      materialTargetsRef.current.clear();
      pieceMeshMapRef.current.clear();
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, [isOpen]);

  // Handle initial file when provided
  useEffect(() => {
    if (isOpen && initialFile) {
      const timer = setTimeout(() => {
        if (sceneRef.current) {
          handleFile(initialFile);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, initialFile]);

  // Update grid when unit changes
  useEffect(() => {
    if (modelLoaded && modelBounds) {
      updateGrid(modelUnit, modelBounds);
    }
  }, [modelUnit, modelLoaded, modelBounds, updateGrid]);

  // Sync modelUnit with app's display unit when dialog opens (before a model is loaded)
  useEffect(() => {
    if (isOpen && !modelLoaded && initialUnit) {
      setModelUnit(mapContextUnitToImporterUnit(initialUnit));
    }
  }, [isOpen, initialUnit, modelLoaded]);

  // Handle file load
  const handleFile = useCallback(async (file) => {
    if (!file) return;

    // Reset state immediately when loading a new model
    setDetectedPieces([]);
    setModelLoaded(false);
    setModelBounds(null);
    setShowObjHint(false);
    setError(null);
    setLoading(true);

    // Check if it's an OBJ file
    const fileIsObj = file.name.toLowerCase().endsWith('.obj');
    setIsObjFile(fileIsObj);

    try {
      // Remove existing model
      if (modelRef.current && sceneRef.current) {
        sceneRef.current.remove(modelRef.current);
        modelRef.current = null;
      }

      // Load the model
      const object = await loadModel(file);

      // Get model bounds (before any transforms)
      const bounds = getModelBounds(object);

      // Auto-detect unit based on model dimensions
      const detectedUnit = autoDetectUnit(object);
      setModelUnit(detectedUnit);

      // Show OBJ hint temporarily if it's an OBJ file
      if (fileIsObj) {
        setShowObjHint(true);
        setTimeout(() => setShowObjHint(false), 8000); // Hide after 8 seconds
      }

      // Center the model at the origin
      object.position.sub(bounds.center);

      // Add to scene
      sceneRef.current.add(object);
      modelRef.current = object;

      // Recalculate bounds after centering and save to state
      const centeredBounds = getModelBounds(object);
      setModelBounds(centeredBounds);

      // Update grid for new model
      updateGrid(detectedUnit, centeredBounds);

      // Zoom to fit the centered model
      zoomToFit(centeredBounds);

      setModelLoaded(true);

      // Analyze the model
      analyzeLoadedModel(object, detectedUnit);

    } catch (err) {
      console.error('Error loading model:', err);
      setError(err.message || 'Failed to load model');
    } finally {
      setLoading(false);
    }
  }, [updateGrid, zoomToFit]);

  // Analyze model for flat pieces
  const analyzeLoadedModel = useCallback((object, unit) => {
    if (!object) return;

    const options = {
      targetThickness: useThicknessFilter ? targetThickness : null,
      thicknessTolerance: 2,
      minArea: 1,
      unit: unit || modelUnit,
      flatnessRatio: 0.4
    };

    const pieces = analyzeModel(object, options);
    setDetectedPieces(pieces);

    // Highlight pieces in 3D view (updateColors=true to assign color indices)
    highlightPieces(object, pieces, true);
  }, [useThicknessFilter, targetThickness, modelUnit]);

  // Re-analyze when settings change
  useEffect(() => {
    if (modelRef.current && modelLoaded) {
      analyzeLoadedModel(modelRef.current, modelUnit);
    }
  }, [useThicknessFilter, targetThickness, modelLoaded, modelUnit, analyzeLoadedModel]);

  // Update 3D highlighting when hover state changes
  useEffect(() => {
    if (modelRef.current && detectedPieces.length > 0) {
      highlightPieces(modelRef.current, detectedPieces, false, hoveredPieceId);
    }
  }, [hoveredPieceId, detectedPieces]);

  // Create grain direction lines for a piece using its stored bounds
  const createGrainLines = (piece, color) => {
    if (!piece.selected || piece.grainDirection === 'none') return null;

    // Use piece's stored bounds if available, otherwise skip
    if (!piece.bounds) return null;

    const size = piece.bounds.size;
    const center = piece.center;

    // Determine which axis to draw lines along based on grain direction
    // Sort sizes to find the "flat" face (smallest dimension is thickness)
    const dims = [
      { axis: 'x', size: size.x },
      { axis: 'y', size: size.y },
      { axis: 'z', size: size.z }
    ].sort((a, b) => b.size - a.size);

    const widthAxis = dims[0].axis;
    const heightAxis = dims[1].axis;
    const thicknessAxis = dims[2].axis;

    // Create line geometry
    const lineGroup = new THREE.Group();
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 2, transparent: true, opacity: 0.6 });

    const numLines = 5;
    // Grain 'height' means grain runs along the second-largest dimension (height)
    // Grain 'width' means grain runs along the largest dimension (width)
    const lineLength = piece.grainDirection === 'height' ? dims[1].size * 0.8 : dims[0].size * 0.8;
    const spacing = (piece.grainDirection === 'height' ? dims[0].size : dims[1].size) * 0.8 / (numLines + 1);

    // Draw lines on both sides of the piece
    const sides = [1, -1];
    for (const side of sides) {
      for (let i = 1; i <= numLines; i++) {
        const points = [];
        const offset = -((numLines + 1) * spacing) / 2 + i * spacing;
        const thicknessOffset = side * (dims[2].size / 2 + 0.01);

        if (piece.grainDirection === 'height') {
          // Lines along height axis (second largest dimension)
          const start = new THREE.Vector3();
          const end = new THREE.Vector3();

          start[widthAxis] = center[widthAxis] + offset;
          start[heightAxis] = center[heightAxis] - lineLength / 2;
          start[thicknessAxis] = center[thicknessAxis] + thicknessOffset;

          end[widthAxis] = center[widthAxis] + offset;
          end[heightAxis] = center[heightAxis] + lineLength / 2;
          end[thicknessAxis] = center[thicknessAxis] + thicknessOffset;

          points.push(start, end);
        } else {
          // Lines along width axis (largest dimension)
          const start = new THREE.Vector3();
          const end = new THREE.Vector3();

          start[widthAxis] = center[widthAxis] - lineLength / 2;
          start[heightAxis] = center[heightAxis] + offset;
          start[thicknessAxis] = center[thicknessAxis] + thicknessOffset;

          end[widthAxis] = center[widthAxis] + lineLength / 2;
          end[heightAxis] = center[heightAxis] + offset;
          end[thicknessAxis] = center[thicknessAxis] + thicknessOffset;

          points.push(start, end);
        }

        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const line = new THREE.Line(geometry, lineMaterial);
        lineGroup.add(line);
      }
    }

    return lineGroup;
  };

  // Highlight detected pieces in the 3D view and assign color indices
  const highlightPieces = (object, pieces, updateColors = false, hoveredId = null) => {
    // Remove old grain lines
    grainLinesRef.current.forEach(line => {
      if (sceneRef.current) sceneRef.current.remove(line);
    });
    grainLinesRef.current = [];

    // Remove old piece overlays (but keep track of materials for reuse)
    pieceOverlaysRef.current.forEach(overlay => {
      if (sceneRef.current) sceneRef.current.remove(overlay);
    });
    pieceOverlaysRef.current = [];

    // Assign color indices if needed (first time or when pieces change)
    if (updateColors) {
      pieces.forEach((piece, index) => {
        piece.colorIndex = index % PIECE_COLORS.length;
      });
      // Clear material targets when colors update (full rebuild)
      materialTargetsRef.current.clear();
    }

    // Group pieces by their mesh UUID to detect combined meshes
    const piecesByMesh = new Map();
    pieces.forEach(piece => {
      if (!piecesByMesh.has(piece.meshUuid)) {
        piecesByMesh.set(piece.meshUuid, []);
      }
      piecesByMesh.get(piece.meshUuid).push(piece);
    });

    // Helper to set target opacity with smooth transition
    const setTargetOpacity = (material, targetOpacity) => {
      materialTargetsRef.current.set(material, targetOpacity);
      // If this is a new material, start at current opacity for smooth transition
      if (material.opacity === undefined) {
        material.opacity = targetOpacity;
      }
    };

    // Clear and rebuild mesh-to-piece mapping
    pieceMeshMapRef.current.clear();

    // Apply materials to meshes
    object.traverse((child) => {
      if (child.isMesh) {
        const meshPieces = piecesByMesh.get(child.uuid);

        if (meshPieces && meshPieces.length === 1) {
          // Single piece in this mesh - color it directly
          const piece = meshPieces[0];
          const colorIndex = piece.colorIndex ?? 0;
          const isHovered = piece.id === hoveredId;
          const baseOpacity = piece.selected ? 1 : 0.3;
          const targetOpacity = hoveredId === null ? baseOpacity : (isHovered ? 1.0 : 0.15);

          // Track mesh-to-piece mapping for raycasting
          pieceMeshMapRef.current.set(child.uuid, piece.id);

          // Reuse existing material if possible, just update target
          if (!child.material || child.material.userData?.pieceId !== piece.id) {
            child.material = new THREE.MeshStandardMaterial({
              color: piece.selected ? PIECE_COLORS[colorIndex].hex : 0x888888,
              transparent: true,
              opacity: materialTargetsRef.current.has(child.material) ? child.material.opacity : targetOpacity
            });
            child.material.userData = { pieceId: piece.id };
          }
          setTargetOpacity(child.material, targetOpacity);
        } else if (meshPieces && meshPieces.length > 1) {
          // Multiple pieces (separated components) - make mesh nearly invisible
          if (!child.material || !child.material.userData?.isMultiPieceMesh) {
            child.material = new THREE.MeshStandardMaterial({
              color: 0xffffff,
              transparent: true,
              opacity: 0.1,
              depthWrite: false
            });
            child.material.userData = { isMultiPieceMesh: true };
          }
        } else {
          // No detected pieces in this mesh
          const targetOpacity = hoveredId === null ? 0.3 : 0.1;
          if (!child.material || !child.material.userData?.isUndetectedMesh) {
            child.material = new THREE.MeshStandardMaterial({
              color: 0xcccccc,
              transparent: true,
              opacity: targetOpacity
            });
            child.material.userData = { isUndetectedMesh: true };
          }
          setTargetOpacity(child.material, targetOpacity);
        }
      }
    });

    // For pieces from combined meshes, add colored box overlays
    pieces.forEach(piece => {
      const meshPieces = piecesByMesh.get(piece.meshUuid);

      // Add colored overlay for pieces in combined meshes
      if (meshPieces && meshPieces.length > 1 && piece.bounds) {
        const colorIndex = piece.colorIndex ?? 0;
        const size = piece.bounds.size;
        const boxGeometry = new THREE.BoxGeometry(size.x * 0.99, size.y * 0.99, size.z * 0.99);
        const isHovered = piece.id === hoveredId;
        const baseOpacity = piece.selected ? 0.7 : 0.15;
        const targetOpacity = hoveredId === null ? baseOpacity : (isHovered ? 0.9 : 0.1);

        const boxMaterial = new THREE.MeshBasicMaterial({
          color: piece.selected ? PIECE_COLORS[colorIndex].hex : 0x888888,
          transparent: true,
          opacity: targetOpacity, // Will be animated by the render loop
          side: THREE.DoubleSide,
          depthWrite: false
        });
        setTargetOpacity(boxMaterial, targetOpacity);

        const boxMesh = new THREE.Mesh(boxGeometry, boxMaterial);
        boxMesh.position.copy(piece.center);
        boxMesh.renderOrder = 1; // Render after other objects
        boxMesh.userData = { pieceId: piece.id }; // Store piece ID for raycasting

        // Track mesh-to-piece mapping for raycasting
        pieceMeshMapRef.current.set(boxMesh.uuid, piece.id);

        if (sceneRef.current) {
          sceneRef.current.add(boxMesh);
          pieceOverlaysRef.current.push(boxMesh);
        }
      }

      // Draw grain lines only when no piece is hovered, or only for the hovered piece
      const shouldShowGrain = hoveredId === null || piece.id === hoveredId;
      if (piece.selected && piece.grainDirection !== 'none' && shouldShowGrain) {
        const grainLines = createGrainLines(piece, PIECE_COLORS[piece.colorIndex ?? 0].hex);
        if (grainLines && sceneRef.current) {
          sceneRef.current.add(grainLines);
          grainLinesRef.current.push(grainLines);
        }
      }
    });
  };

  // Toggle piece selection
  const togglePiece = (pieceId) => {
    setDetectedPieces(prev => {
      const updated = prev.map(p =>
        p.id === pieceId ? { ...p, selected: !p.selected } : p
      );
      if (modelRef.current) {
        highlightPieces(modelRef.current, updated);
      }
      return updated;
    });
  };

  // Select/deselect all pieces
  const toggleAllPieces = (selected) => {
    setDetectedPieces(prev => {
      const updated = prev.map(p => ({ ...p, selected }));
      if (modelRef.current) {
        highlightPieces(modelRef.current, updated);
      }
      return updated;
    });
  };

  // Cycle grain direction for a piece
  const cycleGrainDirection = (pieceId, e) => {
    if (e) e.stopPropagation(); // Don't toggle selection
    setDetectedPieces(prev => {
      const updated = prev.map(p => {
        if (p.id === pieceId) {
          const directions = ['height', 'width', 'none'];
          const currentIndex = directions.indexOf(p.grainDirection);
          const nextIndex = (currentIndex + 1) % directions.length;
          return { ...p, grainDirection: directions[nextIndex] };
        }
        return p;
      });
      if (modelRef.current) {
        highlightPieces(modelRef.current, updated);
      }
      return updated;
    });
  };

  // Handle 3D view mouse move for hover detection
  const handle3DMouseMove = useCallback((e) => {
    if (!containerRef.current || !cameraRef.current || !modelRef.current) return;

    // If mouse button is held down, track as dragging
    if (e.buttons > 0) {
      const dx = e.clientX - mouseDownPosRef.current.x;
      const dy = e.clientY - mouseDownPosRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > 5) {
        isDraggingRef.current = true;
      }
    }

    // Don't update hover state while dragging/orbiting
    if (isDraggingRef.current || e.buttons > 0) {
      setHoveredPieceId(null);
      setTooltipInfo(null);
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

    // Get all meshes to test (model meshes + piece overlays)
    const meshesToTest = [];
    modelRef.current.traverse((child) => {
      if (child.isMesh) meshesToTest.push(child);
    });
    pieceOverlaysRef.current.forEach(overlay => meshesToTest.push(overlay));

    const intersects = raycasterRef.current.intersectObjects(meshesToTest, false);

    if (intersects.length > 0) {
      const hitMesh = intersects[0].object;
      const pieceId = pieceMeshMapRef.current.get(hitMesh.uuid) || hitMesh.userData?.pieceId;

      if (pieceId) {
        const piece = detectedPieces.find(p => p.id === pieceId);
        if (piece) {
          setHoveredPieceId(pieceId);
          setTooltipInfo({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            name: piece.meshName || `Piece ${piece.id}`,
            dimensions: `${formatDisplayDimension(piece.width)} × ${formatDisplayDimension(piece.height)} ${getUnitSymbol(displayUnit)}`
          });
          return;
        }
      }
    }

    // No piece hit
    setHoveredPieceId(null);
    setTooltipInfo(null);
  }, [detectedPieces, modelUnit]);

  // Handle 3D view mouse down to track drag start position
  const handle3DMouseDown = useCallback((e) => {
    mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
    isDraggingRef.current = false;
  }, []);

  // Handle 3D view mouse up
  const handle3DMouseUp = useCallback((e) => {
    // Check if mouse moved significantly (was a drag, not a click)
    const dx = e.clientX - mouseDownPosRef.current.x;
    const dy = e.clientY - mouseDownPosRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If moved more than 5 pixels, it was a drag
    if (distance > 5) {
      isDraggingRef.current = true;
      // Reset after a short delay to allow hover to resume
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 100);
    } else {
      isDraggingRef.current = false;
    }
  }, []);

  // Handle 3D view click to cycle grain direction
  const handle3DClick = useCallback((e) => {
    if (!containerRef.current || !cameraRef.current || !modelRef.current) return;

    // Ignore clicks that were actually drags
    const dx = e.clientX - mouseDownPosRef.current.x;
    const dy = e.clientY - mouseDownPosRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance > 5) return;

    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

    // Get all meshes to test
    const meshesToTest = [];
    modelRef.current.traverse((child) => {
      if (child.isMesh) meshesToTest.push(child);
    });
    pieceOverlaysRef.current.forEach(overlay => meshesToTest.push(overlay));

    const intersects = raycasterRef.current.intersectObjects(meshesToTest, false);

    if (intersects.length > 0) {
      const hitMesh = intersects[0].object;
      const pieceId = pieceMeshMapRef.current.get(hitMesh.uuid) || hitMesh.userData?.pieceId;

      if (pieceId) {
        cycleGrainDirection(pieceId);
      }
    }
  }, []);

  // Get grain direction icon
  const GrainIcon = ({ direction, className }) => {
    if (direction === 'height') return <ArrowUpDown className={className} />;
    if (direction === 'width') return <ArrowLeftRight className={className} />;
    return <Circle className={className} />;
  };

  // State for import in progress
  const [importing, setImporting] = useState(false);

  // Handle import
  const handleImport = async () => {
    // Check server-side rate limit for 3D model import
    setImporting(true);
    const rateCheck = await checkImportAllowed('3d-model');
    if (!rateCheck.allowed) {
      setImporting(false);
      return; // promptUpgrade is called inside checkImportAllowed
    }

    const selectedPieces = detectedPieces.filter(p => p.selected);
    // Convert model units → app's display unit (the ONLY place real unit conversion happens)
    const targetUnit = displayUnit;
    let cutListPieces = convertToCutList(selectedPieces, { modelUnit, targetUnit });

    if (mergeSimilar) {
      // Unit-aware tolerances for merging
      const dimTol = { mm: 12.7, cm: 1.27, m: 0.0127, 'in': 0.5, ft: 0.042 }[targetUnit] || 0.5;
      const thickTol = { mm: 3.2, cm: 0.32, m: 0.0032, 'in': 0.125, ft: 0.01 }[targetUnit] || 0.125;
      cutListPieces = mergeSimilarPieces(cutListPieces, dimTol, thickTol);
    }

    // Capture 3D model snapshot as SVG (with PNG fallback)
    let modelSnapshot = null;
    if (modelRef.current && cameraRef.current) {
      try {
        // Generate base SVG (B&W — no pieceColors means no faces)
        const svgBase = generate3DEdgeSVG(modelRef.current, cameraRef.current, {
          width: 800,
          height: 600,
          edgeThreshold: 15,
        });

        // Build pieceColors map for color version (triggers face generation)
        const pieceColorsMap = new Map();
        detectedPieces.forEach((piece) => {
          if (piece.selected && piece.colorIndex !== undefined) {
            pieceColorsMap.set(piece.meshUuid, PIECE_COLORS[piece.colorIndex].cssHex);
          }
        });

        // Generate color SVG with faces
        const svgWithFaces = generate3DEdgeSVG(modelRef.current, cameraRef.current, {
          width: 800,
          height: 600,
          edgeThreshold: 15,
          pieceColors: pieceColorsMap,
        });

        // Build mesh UUID → piece dimensions mapping for color correlation
        // Uses same dimensions that mergeSimilarPieces will match on
        const meshToPieceDims = {};
        detectedPieces.forEach((piece) => {
          if (piece.selected) {
            meshToPieceDims[piece.meshUuid] = {
              width: piece.width,
              height: piece.height,
              thickness: piece.thickness,
              grainDirection: piece.grainDirection,
            };
          }
        });

        modelSnapshot = { type: 'svg', data: svgBase, dataWithFaces: svgWithFaces, meshToPieceDims, modelUnit };
      } catch (err) {
        console.warn('SVG edge export failed, falling back to PNG snapshot:', err);
        try {
          const pngDataURL = capture3DSnapshot(
            rendererRef.current,
            sceneRef.current,
            cameraRef.current
          );
          modelSnapshot = { type: 'png', data: pngDataURL };
        } catch (pngErr) {
          console.warn('PNG snapshot also failed:', pngErr);
        }
      }
    }

    onImport(cutListPieces, modelSnapshot);
    setImporting(false);
    onClose();
  };

  // Get pieces grouped by thickness
  const getGroupedPieces = () => {
    if (!groupByThicknessEnabled) {
      return [{ thickness: null, avgThickness: null, pieces: detectedPieces }];
    }
    return groupByThickness(detectedPieces, modelUnit);
  };

  // Format dimensions with appropriate decimal places based on unit (for model unit display)
  const formatDimension = (value, unit = modelUnit) => {
    if (value === null || value === undefined) return '';
    // Use more decimals for meters since values are small
    const decimals = unit === 'm' ? 3 : unit === 'cm' ? 2 : 1;
    return value.toFixed(decimals);
  };

  // Format dimensions converted to display unit (app's unit setting)
  // Caps at 3 decimal places, strips trailing zeros
  const formatDisplayDimension = (value) => {
    if (value === null || value === undefined) return '';
    const converted = convertUnit(value, modelUnit, displayUnit);
    return converted.toFixed(3).replace(/\.?0+$/, '');
  };

  // Format dimensions for tooltip with higher precision (up to 6 decimals)
  // Snaps to nearest 1/256" for imperial to eliminate floating point errors
  // const formatTooltipDimension = (value) => {
  //   if (value === null || value === undefined) return '';
  //   let converted = convertUnit(value, modelUnit, displayUnit);
  //   // Snap to nearest 1/256" for imperial units
  //   if (displayUnit === 'in' || displayUnit === 'ft') {
  //     const twoFiftySixths = Math.round(converted * 256);
  //     converted = twoFiftySixths / 256;
  //   }
  //   return converted.toFixed(6).replace(/\.?0+$/, '');
  // };

  // Drag handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    e.target.value = '';
  };

  const stopDragPropagation = (e) => {
    e.stopPropagation();
  };

  const selectedCount = detectedPieces.filter(p => p.selected).length;
  const groupedPieces = getGroupedPieces();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onDragEnter={stopDragPropagation}
      onDragOver={(e) => { e.preventDefault(); stopDragPropagation(e); }}
      onDragLeave={stopDragPropagation}
      onDrop={stopDragPropagation}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <Box className="w-6 h-6 text-accent-400" />
            <h2 className="text-xl font-bold">Import 3D Model</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* 3D Preview */}
          <div className="flex-1 relative bg-gray-100">
            <div
              ref={containerRef}
              className="w-full h-full cursor-pointer"
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onMouseDown={modelLoaded ? handle3DMouseDown : undefined}
              onMouseUp={modelLoaded ? handle3DMouseUp : undefined}
              onMouseMove={modelLoaded ? handle3DMouseMove : undefined}
              onMouseLeave={() => {
                setHoveredPieceId(null);
                setTooltipInfo(null);
                isDraggingRef.current = false;
              }}
              onClick={modelLoaded ? handle3DClick : undefined}
            />

            {/* Tooltip for hovered piece */}
            {tooltipInfo && (
              <div
                className="absolute pointer-events-none bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm z-10"
                style={{
                  left: tooltipInfo.x + 12,
                  top: tooltipInfo.y + 12,
                  transform: 'translateY(-50%)'
                }}
              >
                <p className="font-medium">{tooltipInfo.name}</p>
                <p className="text-gray-300 text-xs">{tooltipInfo.dimensions}</p>
                <p className="text-gray-400 text-xs mt-1">Click to change grain</p>
              </div>
            )}

            {/* Model info overlay */}
            {modelLoaded && modelBounds && (
              <div className="absolute top-4 left-4 bg-white bg-opacity-90 rounded-lg px-3 py-2 text-xs shadow">
                <p className="text-gray-600">
                  Size: {formatDisplayDimension(modelBounds.size.x)} × {formatDisplayDimension(modelBounds.size.y)} × {formatDisplayDimension(modelBounds.size.z)} {getUnitSymbol(displayUnit)}
                </p>
              </div>
            )}

            {/* Drop zone overlay when no model */}
            {!modelLoaded && !loading && (
              <div
                className="absolute inset-0 flex items-center justify-center"
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <label className="cursor-pointer">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-accent-400 hover:bg-accent-50 transition-colors">
                    <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium text-gray-700">Drop 3D model here</p>
                    <p className="text-sm text-gray-500 mt-1">or click to browse</p>
                    <p className="text-xs text-gray-400 mt-3">Supports OBJ, GLTF, GLB</p>
                  </div>
                  <input
                    type="file"
                    accept=".obj,.gltf,.glb"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                </label>
              </div>
            )}

            {/* Loading overlay */}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                <div className="text-center">
                  <div className="animate-spin w-10 h-10 border-4 border-accent-400 border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-gray-600">Loading model...</p>
                </div>
              </div>
            )}

            {/* Error display */}
            {error && (
              <div className="absolute top-4 left-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">Error loading model</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}

            {/* Model controls */}
            {modelLoaded && (
              <div className="absolute bottom-4 left-4 flex gap-2">
                <label className="px-3 py-2 bg-white rounded-lg shadow-md text-sm font-medium cursor-pointer hover:bg-gray-50">
                  <Upload className="w-4 h-4 inline mr-2" />
                  Load New
                  <input
                    type="file"
                    accept=".obj,.gltf,.glb"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={() => {
                    if (modelBounds) {
                      zoomToFit(modelBounds);
                    }
                  }}
                  className="px-3 py-2 bg-white rounded-lg shadow-md text-sm font-medium hover:bg-gray-50"
                >
                  <RotateCcw className="w-4 h-4 inline mr-2" />
                  Reset View
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-80 border-l flex flex-col bg-gray-50">
            {/* Settings */}
            <div className="border-b bg-white">
              <button
                onClick={() => setSettingsCollapsed(!settingsCollapsed)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Detection Settings</h3>
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${settingsCollapsed ? '-rotate-90' : ''}`} />
              </button>

              {!settingsCollapsed && (
              <div className="px-4 pb-4">
              {/* Model unit */}
              <div className="mb-3">
                <label className="text-sm text-gray-600 block mb-1">Model Unit</label>
                <select
                  value={modelUnit}
                  onChange={(e) => setModelUnit(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:border-accent-400 focus:outline-none"
                >
                  {Object.entries(UNIT_INFO).map(([key, info]) => (
                    <option key={key} value={key}>{info.label} ({info.short})</option>
                  ))}
                </select>
                {modelLoaded && (
                  <p className="text-xs text-gray-400 mt-1">
                    Auto-detected from model dimensions
                  </p>
                )}
                {/* OBJ file hint */}
                {isObjFile && showObjHint && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      OBJ files are typically exported in meters, even if modeled in different units.
                    </p>
                  </div>
                )}
              </div>

              {/* Thickness filter */}
              <div className="mb-3">
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useThicknessFilter}
                    onChange={(e) => setUseThicknessFilter(e.target.checked)}
                    className="rounded border-gray-300 text-accent-400 focus:ring-accent-400"
                  />
                  Filter by thickness
                </label>
                {useThicknessFilter && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="number"
                      value={targetThickness}
                      onChange={(e) => setTargetThickness(parseFloat(e.target.value) || 0)}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm focus:border-accent-400 focus:outline-none"
                    />
                    <span className="text-sm text-gray-500">{modelUnit}</span>
                  </div>
                )}
              </div>

              {/* Group by thickness */}
              <div className="mb-3">
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={groupByThicknessEnabled}
                    onChange={(e) => setGroupByThicknessEnabled(e.target.checked)}
                    className="rounded border-gray-300 text-accent-400 focus:ring-accent-400"
                  />
                  Group by thickness
                </label>
              </div>

              {/* Merge similar */}
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mergeSimilar}
                  onChange={(e) => setMergeSimilar(e.target.checked)}
                  className="rounded border-gray-300 text-accent-400 focus:ring-accent-400"
                />
                Merge similar pieces on import
              </label>
              </div>
              )}
            </div>

            {/* Detected Pieces List */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">
                    Detected Pieces ({detectedPieces.length})
                  </h3>
                  <button
                    onClick={() => setShowAllPieces(!showAllPieces)}
                    className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
                    title={showAllPieces ? 'Hide unselected' : 'Show all'}
                  >
                    {showAllPieces ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>

                {detectedPieces.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">
                    {modelLoaded ? 'No flat pieces detected. Try adjusting the unit setting.' : 'Load a model to detect pieces'}
                  </p>
                ) : (
                  <>
                    {/* Select/Deselect all */}
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => toggleAllPieces(true)}
                        className="flex-1 px-3 py-1.5 text-xs font-medium bg-accent-100 text-accent-700 rounded hover:bg-accent-200"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => toggleAllPieces(false)}
                        className="flex-1 px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        Deselect All
                      </button>
                    </div>

                    {/* Pieces list grouped by thickness */}
                    <div className="space-y-4">
                      {groupedPieces.map((group, groupIndex) => (
                        <div key={groupIndex}>
                          {group.avgThickness !== null && (
                            <div className="flex items-center gap-2 mb-2">
                              <Layers className="w-4 h-4 text-gray-400" />
                              <span className="text-xs font-semibold text-gray-600 tracking-wide">
                                {formatDisplayDimension(group.avgThickness)} {getUnitSymbol(displayUnit)} Thick ({group.pieces.length})
                              </span>
                            </div>
                          )}
                          <div className="space-y-2">
                            {group.pieces
                              .filter(piece => showAllPieces || piece.selected)
                              .map((piece) => {
                                const colorInfo = PIECE_COLORS[piece.colorIndex ?? 0];
                                const isHovered = hoveredPieceId === piece.id;
                                return (
                                  <div
                                    key={piece.id}
                                    onClick={() => togglePiece(piece.id)}
                                    onMouseEnter={() => setHoveredPieceId(piece.id)}
                                    onMouseLeave={() => setHoveredPieceId(null)}
                                    className={`w-full text-left p-3 rounded-lg border-2 transition-all cursor-pointer ${
                                      piece.selected
                                        ? 'bg-white'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    } ${isHovered ? 'ring-2 ring-offset-1 ring-accent-400 shadow-md' : ''}`}
                                    style={piece.selected ? { borderColor: colorInfo.cssHex } : undefined}
                                  >
                                    <div className="flex items-start gap-2">
                                      {/* Color indicator */}
                                      <div
                                        className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                                        style={{ backgroundColor: piece.selected ? colorInfo.cssHex : '#D1D5DB' }}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm text-gray-900 truncate">
                                          {piece.meshName || `Piece ${piece.id}`}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          {formatDisplayDimension(piece.width)} × {formatDisplayDimension(piece.height)} {getUnitSymbol(displayUnit)}
                                        </p>
                                        {!groupByThicknessEnabled && (
                                          <p className="text-xs text-gray-400">
                                            Thickness: {formatDisplayDimension(piece.thickness)} {getUnitSymbol(displayUnit)}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 ml-2">
                                        {/* Grain direction button */}
                                        <button
                                          onClick={(e) => cycleGrainDirection(piece.id, e)}
                                          className={`p-1.5 rounded transition-colors ${
                                            piece.grainDirection === 'none'
                                              ? 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                              : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                          }`}
                                          title={`Grain: ${piece.grainDirection} (click to change)`}
                                        >
                                          <GrainIcon direction={piece.grainDirection} className="w-4 h-4" />
                                        </button>
                                        {/* Selection indicator */}
                                        <div
                                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                                          style={{
                                            backgroundColor: piece.selected ? colorInfo.cssHex : '#E5E7EB',
                                            color: piece.selected ? 'white' : undefined
                                          }}
                                        >
                                          {piece.selected && <Check className="w-3 h-3" />}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Import button */}
            <div className="p-4 border-t bg-white">
              <button
                onClick={handleImport}
                disabled={selectedCount === 0 || importing}
                className="w-full py-3 bg-accent-400 text-white rounded-lg font-semibold hover:bg-accent-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Checking...
                  </>
                ) : (
                  `Import ${selectedCount} Piece${selectedCount !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Model3DImporter;
